import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";

/** Phase 2 — PM/Admin desktop Sales Management (read-only list + export).
 *  Approval flow lands in Phase 3.
 */
export default function SalesManagement() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7) + "-01";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ from_date: monthStart, to_date: today, status: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/promoter/sales", {
        params: {
          from_date: filters.from_date || undefined,
          to_date: filters.to_date || undefined,
          status: filters.status || undefined,
        },
      });
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters.status]);

  const exportXlsx = () => {
    const token = localStorage.getItem("access_token");
    const qs = new URLSearchParams();
    if (filters.from_date) qs.set("from_date", filters.from_date);
    if (filters.to_date) qs.set("to_date", filters.to_date);
    if (filters.status) qs.set("status", filters.status);
    fetch(`${API}/promoter/sales/export.xlsx?${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("export failed"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "promoter_sales.xlsx"; a.click();
        URL.revokeObjectURL(url);
      }).catch(() => toast.error("Could not export"));
  };

  const totals = rows.reduce((a, r) => {
    a.amount += Number(r.invoice_amount || 0);
    a.qty += Number(r.quantity || 0);
    return a;
  }, { amount: 0, qty: 0 });

  return (
    <div className="space-y-4" data-testid="sales-management">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Promoter Sales</h1>
          <p className="text-sm text-slate-500 mt-1">Pending and verified sales across all promoters. (Approval workflow arrives in Phase 3.)</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">From</Label>
            <Input type="date" value={filters.from_date}
                   onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
                   className="h-8 w-36" data-testid="sales-from-date" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">To</Label>
            <Input type="date" value={filters.to_date}
                   onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
                   className="h-8 w-36" data-testid="sales-to-date" />
          </div>
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Status</Label>
            <Select value={filters.status || "__all__"}
                    onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__all__" ? "" : v }))}>
              <SelectTrigger className="h-8 w-44" data-testid="sales-status-filter"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                <SelectItem value="pending_verification">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="adjusted">Adjusted</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} data-testid="sales-search-btn">
            <RefreshCw size={14} className="mr-1" /> Search
          </Button>
          <Button variant="outline" onClick={exportXlsx} data-testid="sales-export-btn">
            <Download size={14} className="mr-1" /> Excel
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-3" data-testid="sales-totals">
        <Stat label="Records" value={rows.length} testid="stat-rec" />
        <Stat label="Quantity" value={totals.qty} testid="stat-qty" />
        <Stat label="Invoice value (Rs)" value={Number(totals.amount).toLocaleString()} testid="stat-amount" tone="emerald" />
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sales-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Sale Date</th>
                  <th className="text-left px-3 py-2">Promoter</th>
                  <th className="text-left px-3 py-2">Dealer</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Brand / Model</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-left px-3 py-2">Invoice #</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={9} className="text-center text-slate-400 py-6">Loading…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-6" data-testid="sales-empty">No sales in this range.</td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/50"
                      data-testid={`sales-row-${r.id.slice(0, 8)}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.sale_date}</td>
                    <td className="px-3 py-2">{r.promoter_name} <span className="text-slate-400 text-[10px]">{r.promoter_promoter_id}</span></td>
                    <td className="px-3 py-2 text-slate-600">{r.dealer_name || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="text-slate-900">{r.customer_name}</div>
                      <div className="text-[10px] text-slate-500">{r.customer_phone}</div>
                    </td>
                    <td className="px-3 py-2">{r.brand} {r.product_model}</td>
                    <td className="px-3 py-2 text-right">{r.quantity}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.invoice_number}</td>
                    <td className="px-3 py-2 text-right font-semibold">{Number(r.invoice_amount).toLocaleString()}</td>
                    <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = {
    pending_verification: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    adjusted: "bg-indigo-100 text-indigo-800",
  }[status] || "bg-slate-100 text-slate-700";
  return <Badge className={cls}>{(status || "").replace("_", " ")}</Badge>;
}

function Stat({ label, value, testid, tone }) {
  const toneCls = { emerald: "text-emerald-700" }[tone] || "text-slate-900";
  return (
    <Card data-testid={testid}>
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`font-heading text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
